/**
 * The partner referral code this browser is carrying.
 *
 * Deliberately a sibling of `lib/attribution.ts` rather than a field inside it.
 * A `utm_campaign` is a label the business wrote on its own advertisement; a
 * referral code is a claim on money owed to a named person, and the two are
 * governed differently:
 *
 *  - Attribution has no expiry at all. A referral does, and the server decides
 *    how long it is — this file stores the capture time and lets the server
 *    apply the window, because a value the browser could edit must never be the
 *    thing that decides who gets paid.
 *  - Attribution is written regardless of consent, because it holds no
 *    identifier. So is this, and for the same reason: a partner's own code is
 *    not a fact about the visitor.
 *
 * The code stored here is a *hint*. Everything that matters is re-checked
 * server-side against the persisted referral, which is written once at signup
 * and cannot be reached from the browser at all.
 */

export interface StoredReferral {
  code: string;
  capturedAt: string;
  landingPage?: string;
  /** The course the link pointed at, when it pointed at one. */
  courseSlug?: string;
}

const KEY = 'mdx-referral';

/**
 * Long enough to outlast any window the server is likely to be set to, and
 * short enough that a code sitting in a browser for a year is not silently
 * re-offered. The server's `attributionWindowDays` is the real limit; this is
 * only about not keeping rubbish forever.
 */
const LOCAL_MAX_AGE_MS = 120 * 24 * 60 * 60 * 1000;

/** The same alphabet the server mints codes from, plus a length sanity check. */
const CODE_SHAPE = /^[A-Z0-9]{4,24}$/;

const normalise = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  const cleaned = raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  return CODE_SHAPE.test(cleaned) ? cleaned : null;
};

const read = (): StoredReferral | null => {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredReferral;
    if (!normalise(parsed?.code)) return null;
    if (Date.now() - new Date(parsed.capturedAt).getTime() > LOCAL_MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

const write = (value: StoredReferral): void => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // Storage unavailable — the referral degrades to this page load only, which
    // still works for the common case of clicking a link and buying at once.
  }
};

/**
 * Captures a code from the URL. Called once per page load, before the router
 * rewrites the query string.
 *
 * Two spellings are accepted: `?ref=CODE` on any page, and the `/r/:code` path
 * the printed and spoken links use. `?via=` is accepted too because it is the
 * convention several affiliate networks use and somebody will paste one.
 *
 * **Last touch wins here, unlike attribution's first-touch rule.** A visitor
 * who follows Amaka's link today and Chidi's tomorrow is carrying Chidi's code,
 * and that is correct as a *hint* — but only until they register. At signup the
 * server writes a referral that is unique per user and never rewritten, so the
 * first partner to actually introduce an account keeps them permanently. The
 * asymmetry is deliberate: the browser should offer the freshest claim, and the
 * ledger should honour the earliest one.
 */
export const captureReferral = (): void => {
  if (typeof window === 'undefined') return;

  const query = new URLSearchParams(window.location.search);
  const fromQuery = normalise(query.get('ref') ?? query.get('via'));
  const fromPath = normalise(window.location.pathname.match(/^\/r\/([^/?#]+)/i)?.[1]);
  const code = fromQuery ?? fromPath;
  if (!code) return;

  write({
    code,
    capturedAt: new Date().toISOString(),
    landingPage: window.location.pathname,
    courseSlug: query.get('course') ?? undefined,
  });
};

/** The stored code, or undefined. Safe to spread into a request body. */
export const getReferralCode = (): string | undefined => read()?.code;

export const getStoredReferral = (): StoredReferral | null => read();

/**
 * Forgets the code.
 *
 * Called after a successful registration, because from that moment the server's
 * own referral row is the record and a stale code in storage can only cause
 * confusion — a second account created on the same browser would otherwise
 * offer a partner a referral they did not earn.
 */
export const clearReferral = (): void => {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
};

/** Builds a shareable partner link, the same way the dashboard does. */
export const partnerLink = (code: string, courseSlug?: string): string => {
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  return courseSlug ? `${origin}/r/${code}?course=${encodeURIComponent(courseSlug)}` : `${origin}/r/${code}`;
};
